/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { ActionsObservable, combineEpics } from "redux-observable";
import { merge, never, Observable, of } from "rxjs";
import { ajax } from "rxjs/ajax";
import { catchError, exhaustMap, filter, map, pluck, switchMap, tap } from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import { AppAction } from "../";
import config from "../../config";
import { CURRENT_USER } from '../../utils/types';
import { IdentityToken, OAuthConfiguration } from "./../../utils/auth";
import { fetchOAuthConfigActions, fetchTokenActions, redirect, signOutAction, tokenCleared } from "./actions";

const OAUTH_CONFIG_STORAGE_KEY = "oauthConfig";
const OAUTH_TOKEN_STORAGE_KEY = "authToken";

const redirectEpic = (action$: ActionsObservable<AppAction>) => {
  return action$.pipe(
    filter(isActionOf(redirect)),
    switchMap(action => {
      action.payload.history.push(action.payload.uri);
      return never();
    })
  );
};

/**
 * Fetch the oauth configuration and persist it in localStorage. It is also reduced into the session state.
 */
const oauthConfigEpic = (action$: ActionsObservable<AppAction>) => {
  return action$.pipe(
    filter(isActionOf(fetchOAuthConfigActions.request)),
    switchMap(action => {
      const authConfig = localStorage.getItem(OAUTH_CONFIG_STORAGE_KEY);
      if (authConfig !== null)
        return of(fetchOAuthConfigActions.success(JSON.parse(authConfig)));

      return (ajax.getJSON(
        `${action.payload}/.well-known/openid-configuration`
      ) as Observable<OAuthConfiguration>).pipe(
        tap(
          (config: OAuthConfiguration) =>
            localStorage.setItem(
              OAUTH_CONFIG_STORAGE_KEY,
              JSON.stringify(config)
            ) // cache the oauth config for later
        ),
        map(config => fetchOAuthConfigActions.success(config)),
        catchError(error => of(fetchOAuthConfigActions.failure(error)))
      );
    })
  );
};

/**
 * Fetch a /dfdm access token via authorization_code grant, exchange it for an access token for /forms,
 * persist both tokens in localStorage.
 */
const tokenEpic = (action$: ActionsObservable<AppAction>) => {
  return action$.pipe(
    filter(isActionOf(fetchTokenActions.request)),
    exhaustMap(action => {
      const { code, oauthConfig, history } = action.payload;

      const cachedToken = sessionStorage.getItem(OAUTH_TOKEN_STORAGE_KEY);

      if (cachedToken) {
        return of(
          fetchTokenActions.success({
            dfdmToken: JSON.parse(cachedToken)
          }),
          redirect("/", history)
        );
      }
      return ajax
        .post(oauthConfig.token_endpoint, {
          grant_type: "authorization_code",
          code: code,
          redirect_uri: config.oauth.redirect_uri,
          client_id: config.oauth.client_id,
          client_secret: config.oauth.client_secret
        })
        .pipe(
          map(resp => resp.response as IdentityToken),
          tap(token => {
            localStorage.setItem(
              OAUTH_TOKEN_STORAGE_KEY,
              JSON.stringify(token)
            );
          }),
          switchMap(token =>
            of(
              fetchTokenActions.success({ dfdmToken: token }),
              redirect("/", history)
            )
          ),
          catchError(error => of(fetchTokenActions.failure(error)))
        );
    })
  );
};

const signOutEpic = (action$: ActionsObservable<AppAction>) => {
  return merge(
    action$.pipe(filter(isActionOf(signOutAction))),  // explicit sign out action dispatched
    action$.pipe( // some network request came back with a 401
      pluck("payload"),
      filter(payload => {
        if (typeof payload !== "object" || payload === null) return false;

        return (
          payload.hasOwnProperty("status") &&
          (payload as { status: any }).status === 401
        );
      })
    )
  ).pipe(
    tap(function clearTokenCache() {
      localStorage.removeItem(CURRENT_USER);
      localStorage.removeItem(OAUTH_TOKEN_STORAGE_KEY);
    }),
    map(tokenCleared)
  );
};

export const sessionEpics = combineEpics(
  redirectEpic,
  oauthConfigEpic,
  tokenEpic,
  signOutEpic
);
